import React from "react";
import {
  CreditCard,
  Search,
  CheckCircle,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
  ArrowRight,
} from "lucide-react";

const HowItWorks = () => {
  return (
    <section className="py-20 bg-slate-50 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-voyage-cyan/10 rounded-full blur-[120px] animate-blob" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-slate-primary/5 rounded-full blur-[120px] animate-blob [animation-delay:2s]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="bg-white/80 backdrop-blur-md rounded-[3rem] p-10 border border-white shadow-sm mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight">
              How Just For Points Works
            </h2>
            <p className="text-slate-500 font-bold uppercase tracking-widest text-[10px]">
              Three simple steps to smarter travel
            </p>
          </div>

          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            {[
              {
                icon: <CreditCard />,
                title: "Enter Your Points & Trip",
                desc: "Select your card, points balance, destination, dates, and cabin class.",
              },
              {
                icon: <Search />,
                title: "We Analyze Everything",
                desc: "Just For Points scans flights, transfer partners, and real-time value data.",
              },
              {
                icon: <CheckCircle />,
                title: "Get Smart Recommendations",
                desc: "See the best (and worst) redemptions with scores and clear explanations.",
              },
            ].map((step, i) => (
              <React.Fragment key={i}>
                <div className="flex-1 flex items-start gap-4 p-4 rounded-3xl transition-all hover:bg-white group">
                  <div className="relative shrink-0">
                    <div className="w-14 h-14 bg-slate-primary text-white rounded-2xl flex items-center justify-center shadow-lg shadow-slate-200 group-hover:scale-110 transition-transform">
                      {React.cloneElement(step.icon, {
                        className: "text-voyage-cyan",
                      })}
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-slate-primary border-2 border-white rounded-full flex items-center justify-center text-[10px] font-black text-voyage-cyan">
                      {i + 1}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-black text-slate-900 mb-1 leading-tight">
                      {step.title}
                    </h3>
                    <p className="text-slate-400 text-[11px] font-bold leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>
                {i < 2 && (
                  <ChevronRight
                    className="hidden lg:block text-slate-200 animate-pulse"
                    size={20}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="mt-16 bg-white rounded-[3rem] overflow-hidden border border-slate-100 shadow-sm">
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-primary to-slate-900 p-12 text-white">
            <div className="max-w-4xl mx-auto">
              <span className="inline-flex px-4 py-2 rounded-full bg-white/10 text-voyage-cyan text-xs font-black uppercase tracking-widest mb-6">
                Travel Rewards Intelligence
              </span>

              <h2 className="text-4xl font-black leading-tight mb-4">
                Why Smart Travelers Care About Point Value
              </h2>

              <p className="text-slate-300 text-lg max-w-3xl">
                The difference between a good redemption and a great redemption
                can be hundreds or even thousands of dollars.
              </p>
            </div>
          </div>

          <div className="p-12">
            {/* Stats */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-emerald-50 p-6 rounded-3xl border border-emerald-100">
                <div className="text-4xl font-black text-emerald-600 mb-2">
                  2x+
                </div>
                <p className="text-slate-600 font-medium">
                  Higher value often available through transfer partners
                </p>
              </div>

              <div className="bg-orange-50 p-6 rounded-3xl border border-orange-100">
                <div className="text-4xl font-black text-orange-500 mb-2">
                  40%
                </div>
                <p className="text-slate-600 font-medium">
                  Of value can be lost with poor redemption choices
                </p>
              </div>

              <div className="bg-cyan-50 p-6 rounded-3xl border border-cyan-100">
                <div className="text-4xl font-black text-cyan-600 mb-2">
                  $1000+
                </div>
                <p className="text-slate-600 font-medium">
                  Potential savings for frequent travelers
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto space-y-10">
              <div>
                <h3 className="text-2xl font-black text-slate-900 mb-4">
                  Every Point Has a Different Value
                </h3>

                <p className="text-slate-600 leading-8 text-lg">
                  Reward points can be incredibly valuable when used
                  strategically, but many travelers unknowingly redeem them for
                  far less than they're worth. The same number of points can
                  produce dramatically different results depending on how they
                  are redeemed.
                </p>
              </div>

              <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100">
                <h4 className="font-black text-slate-900 mb-3">Example:</h4>

                <p className="text-slate-600 leading-8">
                  50,000 points redeemed for cash back may provide around $500
                  in value. Those same points transferred strategically to an
                  airline partner could unlock premium flights worth $900 or
                  more.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-black text-slate-900 mb-4">
                  Why Most Travelers Leave Value Behind
                </h3>

                <p className="text-slate-600 leading-8 text-lg">
                  Loyalty programs often make redemption options confusing.
                  Travelers frequently choose convenience over value because
                  comparing transfer partners, award charts, and travel portals
                  takes time and expertise.
                </p>
              </div>

              <div className="border-l-4 border-voyage-cyan pl-6 py-2">
                <p className="text-xl font-bold text-slate-800 italic leading-relaxed">
                  "Two travelers with the same 50,000 points can receive
                  completely different outcomes depending on how they redeem."
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-black text-slate-900 mb-4">
                  How Just For Points Helps
                </h3>

                <p className="text-slate-600 leading-8 text-lg">
                  Just For Points analyzes transfer partners, redemption values,
                  and loyalty programs to help travelers identify opportunities
                  that maximize value while avoiding common redemption mistakes.
                </p>
              </div>
            </div>
          </div>
        </div>

        <section className="mt-12">
          <h2 className="text-3xl font-black mb-8">
            Common Redemption Mistakes
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-8 rounded-3xl border">
              <h3 className="font-black mb-3">Redeeming Without Comparing</h3>
              <p className="text-slate-600">
                Many travelers use the first redemption option they see without
                comparing transfer partners or alternative booking methods.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border">
              <h3 className="font-black mb-3">Ignoring Transfer Bonuses</h3>
              <p className="text-slate-600">
                Limited-time transfer bonuses can significantly increase
                redemption value when used strategically.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border">
              <h3 className="font-black mb-3">
                Using Points for Low-Value Purchases
              </h3>
              <p className="text-slate-600">
                Gift cards and merchandise often provide lower value than travel
                redemptions.
              </p>
            </div>
          </div>
        </section>

        <section className="mt-20">
          <h2 className="text-3xl font-black mb-10">Popular Reward Programs</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Chase Ultimate Rewards",
              "American Express Membership Rewards",
              "Capital One Miles",
              "Citi ThankYou Rewards",
              "Marriott Bonvoy",
              "World of Hyatt",
            ].map((program) => (
              <div
                key={program}
                className="bg-white p-8 rounded-3xl border border-slate-100"
              >
                <h3 className="font-black text-lg mb-3">{program}</h3>
                <p className="text-slate-600">
                  Learn how to maximize redemption value and identify the best
                  transfer opportunities.
                </p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-20 bg-white rounded-[3rem] p-10 border border-slate-100">
          <h2 className="text-3xl font-black text-slate-900 mb-10">
            Frequently Asked Questions About Reward Points
          </h2>

          <div className="space-y-8 text-slate-600 leading-8">
            <div>
              <h3 className="text-xl font-black text-slate-900 mb-2">
                How do I know if my redemption is good or bad?
              </h3>
              <p>
                A good redemption depends on the value you receive per point. If
                you are getting significantly less value compared to travel
                redemptions or transfer partners, it is usually considered a
                low-value redemption. Comparing multiple options before booking
                helps you make better decisions.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-black text-slate-900 mb-2">
                Why do airlines and hotels give different point values?
              </h3>
              <p>
                Each airline and hotel program has its own pricing system. Some
                use fixed award charts, while others use dynamic pricing that
                changes based on demand, season, and availability. This is why
                the same number of points can have different value depending on
                where and how they are used.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-black text-slate-900 mb-2">
                Should I always save my points for business class flights?
              </h3>
              <p>
                Not always. While business class often provides high value, the
                best redemption depends on your travel goals. Sometimes economy
                flights or hotel stays can offer better flexibility and overall
                value depending on pricing and availability.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-black text-slate-900 mb-2">
                Do credit card points expire?
              </h3>
              <p>
                Many credit card rewards programs do not expire as long as your
                account remains open and in good standing. However, airline and
                hotel loyalty points may have expiration rules depending on
                account activity.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-black text-slate-900 mb-2">
                What is the safest way to maximize point value?
              </h3>
              <p>
                The safest strategy is to compare at least two redemption
                options before booking. This usually includes checking both cash
                prices and award options through transfer partners or travel
                portals to ensure you are getting strong value.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-black text-slate-900 mb-2">
                How often should I check point valuations?
              </h3>
              <p>
                Point values can change frequently due to airline pricing, hotel
                demand, and transfer bonuses. It is recommended to check
                valuations each time you plan a redemption rather than relying
                on old estimates.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
